/* eslint-disable jsx-a11y/anchor-is-valid */
import {useIntl} from 'react-intl'
import {useEffect, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {useSearchParams} from 'react-router-dom'
import {StepProps} from '../../../../../app/modules/project/components/core/_models'
import {Client} from '../../../../../app/modules/clients/components/core/_models'
import {getClients} from '../../../../../app/modules/clients/components/core/_requests'
import {QUERIES, stringifyRequestQuery} from '../../../../helpers'
import {useQueryRequest} from '../../../../../app/modules/project/components/core/QueryRequestProvider'
import {SelectAutoComplete} from '../../../components/Select/SelectAutoComplete'
import {ClientEditModal} from '../../client-edit-modal/ClientEditModal'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import Select, {MultiValue} from 'react-select'
import {getTeams} from '../../team/core/_requests'
import clsx from 'clsx'

const Step1 = ({data, updateData, hasError}: StepProps) => {
  const intl = useIntl()
  const [clients, setClients] = useState<Client[]>([])
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const [showClientEditModal, setShowClientEditModal] = useState<boolean>(false)
  const [initialClientName, setInitialClientName] = useState<string | undefined>(undefined)
  const [searchParams, setSearchParams] = useSearchParams()

  const getSelectClients = useMutation(() => getClients(query), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: (response) => {
      // ✅ update detail view directly
      setClients(response.data)
    },
  })

  useEffect(() => {
    getSelectClients.mutateAsync()
  }, [])

  const handleCreateClient = (newClientName: string) => {
    setInitialClientName(newClientName)
    setShowClientEditModal(true)
  }

  const handleCloseClientEditModal = () => {
    setShowClientEditModal(false)
    setInitialClientName(undefined)
  }

  const {data: dataTeam} = useQuery(
    `${QUERIES.TEAMS_LIST}-all`,
    async () => (await getTeams()).data,
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  return (
    <>
      <div className='current'>
        <div className='d-flex flex-column w-100 gap-4'>
          {/*begin::Form Group */}
          <div className='fv-row'>
            <label
              htmlFor='project-name'
              className='d-flex align-items-center fs-5 fw-semibold mb-2'
            >
              <span className='required'>
                {intl.formatMessage({id: 'FORM.INPUT.NAME.PROJECT_NAME'})}
              </span>
              <i
                className='fas fa-exclamation-circle ms-2 fs-7'
                data-bs-toggle='tooltip'
                title={intl.formatMessage({id: 'FORM.INPUT.TOOLTIP.PROJECT_NAME'})}
              ></i>
            </label>
            <input
              type='text'
              className='form-control text-gray-900 form-control-lg form-control-solid'
              name='project-name'
              id='project-name'
              placeholder=''
              autoComplete='off'
              value={data.name ?? ''}
              onChange={(e: any) => {
                updateData({
                  name: e.target.value,
                })
              }}
            />
            {!data.name && hasError && (
              <div className='fv-plugins-message-container'>
                <div data-field='project-name' data-validator='notEmpty' className='fv-help-block'>
                  {intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})}
                </div>
              </div>
            )}
          </div>
          {/*end::Form Group */}

          {/*begin::Form Group */}
          <div className='fv-row'>
            <label
              htmlFor='project-client'
              className='d-flex align-items-center fs-5 fw-semibold mb-2'
            >
              <span className='required'>{intl.formatMessage({id: 'FORM.INPUT.NAME.CLIENT'})}</span>
              <i
                className='fas fa-exclamation-circle ms-2 fs-7'
                data-bs-toggle='tooltip'
                title={intl.formatMessage({id: 'FORM.INPUT.TOOLTIP.CLIENT'})}
              ></i>
            </label>
            <SelectAutoComplete
              endPoint='clientes'
              placeholder={intl.formatMessage({id: 'FORM.INPUT.TOOLTIP.CLIENT'})}
              onChange={(e) => {
                updateData({
                  client_id: e?.value,
                })
              }}
              onCreateOption={(inputValue) => handleCreateClient(inputValue)}
              // async={true}
            />
            {!data.client_id && hasError && (
              <div className='fv-plugins-message-container'>
                <div
                  data-field='project-client'
                  data-validator='notEmpty'
                  className='fv-help-block'
                >
                  {intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})}
                </div>
              </div>
            )}
          </div>
          {/*end::Form Group */}

          <div className='fv-row'>
            <label htmlFor='team' className='d-flex align-items-center fs-5 fw-semibold mb-2'>
              <span className=''>{intl.formatMessage({id: 'FORM.INPUT.NAME.TEAMS'})}</span>
              <OverlayTrigger
                placement='top'
                delay={{show: 50, hide: 100}}
                overlay={
                  <Tooltip>
                    Apenas os usuários da equipe associada ao projeto poderão visualizar suas tarefas
                  </Tooltip>
                }
              >
                <i className='fas fa-exclamation-circle ms-2 fs-7'></i>
              </OverlayTrigger>
            </label>
            <Select
              menuPosition='fixed'
              closeMenuOnSelect={false}
              isSearchable={false}
              options={dataTeam}
              isMulti={true}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name!)}
              getOptionValue={(option) => (typeof option === 'string' ? option : String(option.id))}
              noOptionsMessage={() => 'Nenhuma equipe disponível'}
              onChange={(values) => {
                updateData({
                  team_ids: values.map((item) => item.id),
                })
              }}
              // value={selectedOptions}
              placeholder={'Selecione as equipes'}
              classNames={{
                control: (state) => clsx('border form-control form-control-solid p-1'),
                input: (state) => 'text-dark form-control form-control-solid',
                singleValue: (state) => 'text-dark form-control form-control-solid',
                menu: (state) => 'bg-body',
              }}
              styles={{
                option: (styles, {data, isDisabled, isFocused, isSelected}) => {
                  return {
                    ...styles,
                    backgroundColor: isDisabled
                      ? undefined
                      : isSelected
                      ? '#369bff70'
                      : isFocused
                      ? '#369bff70'
                      : undefined,
                    color: isDisabled ? '#ccc' : isSelected ? '#181C32' : undefined,
                  }
                },
                multiValue: (provided: any) => ({
                  ...provided,
                  borderRadius: '12px',
                  backgroundColor: '#007bff',
                  padding: '0px 4px',
                }),
                multiValueLabel: (provided: any) => ({
                  ...provided,
                  color: 'white',
                }),
                multiValueRemove: (provided: any) => ({
                  ...provided,
                  padding: '0px',
                  margin: '0px',
                  color: 'white',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'none',
                  },
                }),
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: 160,
                  overflowY: 'auto',
                }),
              }}
            />
            {/* end::Input */}
          </div>
        </div>
      </div>
      <ClientEditModal
        show={showClientEditModal}
        handleClose={handleCloseClientEditModal}
        clientId={data.clientId}
        initialClientName={initialClientName}
      />
    </>
  )
}

export {Step1}
